import type { LocationAccess } from "@/lib/location-access";

/**
 * What a "Try again" press may do about a location failure, given what is
 * actually standing in the way.
 *
 * Its own module with only a type import, for the same reason as
 * `nearby-recovery.ts`: this is a decision table, and a table re-implemented at
 * each screen that needs it is how the home screen ended up with a Try again
 * button that re-read the same refusal forever. `getCurrentCoords` never
 * prompts by design — asking is reserved for a user gesture — so any screen
 * that owns such a gesture must decide between asking, deep-linking to
 * settings, and showing instructions. That decision lives here, once, and a
 * plain `node --test` drives every row of it.
 *
 * ## The bug this exists to prevent
 *
 * A user in limited mode (permissions step completed without a grant) hit the
 * "We can't find your location right now" screen. Try again called
 * `refreshVenue`, which read the permission, found it still missing, and
 * repainted the same screen — the OS prompt could never come back, and the only
 * escape was the browser's own site controls, which nothing pointed at.
 */
export type LocationRetryAction =
  /** Ask now. Only valid while the press is still the reason anything happens. */
  | "request"
  /** The OS has stopped asking; only the app's settings page can change it. */
  | "open-app-settings"
  /** The device-wide switch is off, which is a different screen again. */
  | "open-location-settings"
  /** The browser holds the answer and only written steps can get it changed. */
  | "show-steps"
  /** Nothing is refused — the failure was a read, so just read again. */
  | "refresh";

export function locationRetryAction(
  access: LocationAccess,
  isWeb: boolean,
): LocationRetryAction {
  switch (access) {
    case "checking":
    // "checking" asks rather than waits: the check that is still in flight
    // cannot be told about this press, and a request subsumes it — the
    // permissions screen makes the same call for the same reason.
    case "permission-required":
      return "request";

    case "permission-blocked":
      // A request here resolves instantly with no prompt, which reads as a
      // dead button — the exact failure this module exists to remove.
      return isWeb ? "show-steps" : "open-app-settings";

    case "services-disabled":
      return isWeb ? "show-steps" : "open-location-settings";

    case "web-insecure":
    case "web-unsupported":
      // Nothing the app can do; the steps say what the user can.
      return "show-steps";

    case "ready":
    case "error":
      // Ready: permission is fine, the failure was the read itself.
      // Error: the check failed, so the only honest move is to look again.
      return "refresh";
  }
}
